import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiManager from "../../ApiManager/ApiManager";
import "./ContractForm.css";
import { ShowButton } from "../../components/RoleBasedComponents";
import PERMISSIONS from "../../permissions";
import TopNavbar from "../../components/TopNavbar/TopNavbar";
import CustomSelect from "../../components/CustomSelect";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { toast, ToastContainer } from "react-toastify";

const ContractForm = () => {
  const { rowId } = useParams(); // Retrieve the rowId from the URL
  const [city, setCity] = useState("all");
  const [carType, setCarType] = useState({});
  const [tripTypes, setTripTypes] = useState({});
  const [formData, setFormData] = useState({
    contractDates: { startDate: "2019-01-01", endDate: "2019-12-31" },
    tripTypesKmHr: [],
    tripTypesKmDay: [],
    extraAllowance: {},
  });

  const [carGroups, setCarGroups] = useState([]);
  const [cities, setCities] = useState([]);
  const navigate = useNavigate();

  const { loadingStates, loadingMessages, withLoading, getMessage, isLoading } =
    useComponentLoading({
      fetchCities: false,
      fetchCarGroups: false,
      fetchTripTypes: false,
      fetchData: false,
      submitForm: false,
    });

  useEffect(() => {
    const fetchCities = async () => {
      try {
        await withLoading(
          "fetchCities",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getAllCities(params);
            if (!response.ok) throw new Error("Network response was not ok");
            const json = await response.json();
            setCities(json || []);
          },
          "Fetching cities..."
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        setCities([]);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    const fetchCarGroups = async () => {
      try {
        await withLoading(
          "fetchCarGroups",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getCarType(params);
            if (!response.ok) throw new Error("Network response was not ok");
            const json = await response.json();
            setCarGroups(json || []);
            setCarType(json[0]);
          },
          "Fetching car groups..."
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        setCarGroups([]);
        setCarType({});
      }
    };

    const fetchTripTypes = async () => {
      try {
        await withLoading(
          "fetchTripTypes",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const response = await ApiManager.getAllTripTypes(params);
            if (!response.ok) throw new Error("Network response was not ok");
            const json = await response.json();
            setTripTypes(json || []);
          },
          "Fetching trip types..."
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        setTripTypes([]);
      }
    };

    fetchCarGroups();
    fetchTripTypes();
  }, []);

  useEffect(() => {
    if (!carType?.id || !city) return;
    const fetchData = async () => {
      try {
        await withLoading(
          "fetchData",
          async () => {
            const params = new URLSearchParams({
              page_size: 1000,
              page: 1,
            });
            const carGroupsResponse = await ApiManager.getCarType(params);
            const carGroupsData = await carGroupsResponse.json();
            console.log("car groups fetched:", carGroupsData);
            setCarGroups(carGroupsData);
            console.log("Row ID in contract:", rowId);
            const query = new URLSearchParams({
              company_id: rowId,
              car_type_id: carType.id,
              city: city,
            });
            const currentContractRatesResponse =
              await ApiManager.getContractRates(query);
            const currentContractRatesData =
              await currentContractRatesResponse.json();

            const tripTypesKmHr =
              currentContractRatesData.ratesOfTripTypes.filter((trip) =>
                trip.type.includes("hr")
              );
            const tripTypesKmDay =
              currentContractRatesData.ratesOfTripTypes.filter((trip) =>
                trip.type.includes("day")
              );
            const extraAllowance = currentContractRatesData;

            setFormData((prevData) => ({
              ...prevData,
              tripTypesKmHr,
              tripTypesKmDay,
              extraAllowance,
            }));
          },
          "Fetching contract data..."
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [carType, city]);

  // handleCarType change function
  const handleCarTypeChange = (e) => {
    const selectedCarGroupName = e.target.value;
    const selectedCarGroup = carGroups.find(
      (group) => group.name === selectedCarGroupName
    );
    setCarType(selectedCarGroup);
  };

  // handleCityChange function
  const handleCityChange = (e) => {
    const selectedCity = e.target.value;
    setCity(selectedCity);
  };

  const handleInputChangeTripTypes = (index, field, value, type) => {
    console.log(`Updating ${field} of trip type at index ${index} to ${value}`);
    const updatedTripTypes =
      type === "KmHr"
        ? [...formData.tripTypesKmHr]
        : [...formData.tripTypesKmDay];
    const updateNestedField = (obj, fieldPath, value) => {
      const fields = fieldPath.split(".");
      let current = obj;
      for (let i = 0; i < fields.length - 1; i++) {
        current = current[fields[i]];
      }
      current[fields[fields.length - 1]] = value;
    };

    updateNestedField(updatedTripTypes[index], field, value);

    // Update the state
    setFormData({
      ...formData,
      [type === "KmHr" ? "tripTypesKmHr" : "tripTypesKmDay"]: updatedTripTypes,
    });
  };

  // handleInputChange of allowance fields
  const handleInputChangeAllowance = (field, value) => {
    console.log(`Updating ${field} to ${value}`);
    setFormData({
      ...formData,
      extraAllowance: {
        ...formData.extraAllowance,
        [field]: value,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await withLoading(
        "submitForm",
        async () => {
          console.log("Form submitted with data:", formData);
          const query = new URLSearchParams({
            company_id: rowId,
            car_type_id: carType.id,
            city: city,
          });
          const contract = {
            companyId: parseInt(rowId, 10),
            city: city,
            carTypeId: parseInt(carType.id, 10),
            ratesOfTripTypes: [
              ...formData.tripTypesKmHr.map((trip) => {
                console.log("Trip (KM/HR):", trip);
                return {
                  tripTypeId: trip.tripTypeId,
                  name: trip.name,
                  type: trip.type,
                  rate: {
                    base_rates: parseInt(trip.rate.base_rates, 10),
                    extra_km_rates: parseInt(trip.rate.extra_km_rates, 10),
                    extra_hr_rates: parseInt(trip.rate.extra_hr_rates, 10),
                    auto_switch_hr: parseInt(trip.rate.auto_switch_hr, 10),
                    auto_switch_km: parseInt(trip.rate.auto_switch_km, 10),
                    auto_switch_trip_type_id: parseInt(
                      trip.rate.auto_switch_trip_type_id,
                      10
                    ),
                  },
                };
              }),
              ...formData.tripTypesKmDay.map((trip) => {
                console.log("Trip (KM/Day):", trip);
                return {
                  tripTypeId: trip.tripTypeId,
                  name: trip.name,
                  type: trip.type,
                  rate: {
                    base_rates: parseInt(trip.rate.base_rates, 10),
                    extra_km_rates: parseInt(trip.rate.extra_km_rates, 10),
                    extra_day_rates: parseInt(trip.rate.extra_day_rates, 10),
                    auto_switch_km: parseInt(trip.rate.auto_switch_km, 10),
                    auto_switch_day: parseInt(trip.rate.auto_switch_day, 10),
                    auto_switch_trip_type_id: parseInt(
                      trip.rate.auto_switch_trip_type_id,
                      10
                    ),
                  },
                };
              }),
            ],
            overtime_per_hour: parseInt(
              formData.extraAllowance.overtime_per_hour,
              10
            ),
            outstation_allowance: parseInt(
              formData.extraAllowance.outstation_allowance,
              10
            ),
            sunday_allowance: parseInt(
              formData.extraAllowance.sunday_allowance,
              10
            ),
            night_allowance: parseInt(
              formData.extraAllowance.night_allowance,
              10
            ),
            driver_daily_allowance: parseInt(
              formData.extraAllowance.driver_daily_allowance,
              10
            ),
            outstation_overtime_allowance: parseInt(
              formData.extraAllowance.outstation_overtime_allowance,
              10
            ),
            outstation_night_allowance: parseInt(
              formData.extraAllowance.outstation_night_allowance,
              10
            ),
          };
          const submitRatesResponse =
            await ApiManager.createUpdateContractRates(query, contract);
          console.log("submitRatesResponse:", submitRatesResponse);
          toast.success("Contract updated successfully");
        },
        "Submitting contract..."
      );
    } catch (error) {
      console.error("Error submitting contract:", error);
      toast.error("Failed to update contract");
    }
  };

  if (
    loadingStates.fetchCities ||
    loadingStates.fetchCarGroups ||
    loadingStates.fetchTripTypes ||
    loadingStates.fetchData
  ) {
    return (
      <div>
        <LoadingSpinner
          size="large"
          message={getMessage("fetchData") || "Loading contract data..."}
        />
      </div>
    );
  }

  return (
    <>
      <TopNavbar title="Custom Pricing" />
      <ToastContainer />
      <form className="contract-form-container" onSubmit={handleSubmit}>
        <div className="header-container">
          <div className="form-group" style={{ width: "100%" }}>
            <label>Contract Dates: </label>
            <div className="date-input-group">
              <input
                type="date"
                value={formData.contractDates.startDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contractDates: {
                      ...formData.contractDates,
                      startDate: e.target.value,
                    },
                  })
                }
                className="date-input"
              />
              <span className="date-separator">to</span>
              <input
                type="date"
                value={formData.contractDates.endDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contractDates: {
                      ...formData.contractDates,
                      endDate: e.target.value,
                    },
                  })
                }
                min={
                  formData.contractDates.startDate ||
                  new Date().toISOString().split("T")[0]
                } // End date can't be before start date
                className="date-input"
              />
              <button
                className="custom-button"
                onClick={(e) => {
                  e.preventDefault();
                  // Add any additional date editing functionality here
                }}
              >
                Edit dates
              </button>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="form-group">
              <label>Car Type: </label>
              <CustomSelect
                options={carGroups.map((group) => ({
                  value: group.name,
                  label: group.name,
                }))}
                value={carType?.name}
                onChange={(option) => {
                  if (option) {
                    const selectedCarGroup = carGroups.find(
                      (group) => group.name === option.value
                    );
                    setCarType(selectedCarGroup);
                  } else {
                    setCarType(null);
                  }
                }}
                placeholder="Select Car Type"
              />
            </div>
            <div className="form-group">
              <label>City: </label>
              <CustomSelect
                options={[
                  { value: "all", label: "All" },
                  ...cities.map((city) => ({ value: city.name, label: city.name })),
                ]}
                value={city}
                onChange={(option) => setCity(option ? option.value : "")}
                placeholder="Select City"
              />
            </div>
          </div>
        </div>
        <h4>Trip Types (KM/HR)</h4>
        <div className="table-container-wrapper">
          <table className="table-container">
            <thead>
              <tr>
                <th>Trip Types</th>
                <th>Base Rate</th>
                <th>Extra KM Rate</th>
                <th>Extra HR Rate</th>
                <th>Auto switch HR</th>
                <th>Auto switch KM</th>
                <th>Auto switch Trip Type</th>
              </tr>
            </thead>
            <tbody>
              {formData.tripTypesKmHr.map((tripType, index) => (
                <tr key={index}>
                  <td>{tripType.name}</td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.base_rates || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.base_rates",
                          e.target.value,
                          "KmHr"
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.extra_km_rates || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.extra_km_rates",
                          e.target.value,
                          "KmHr"
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.extra_hr_rates || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.extra_hr_rates",
                          e.target.value,
                          "KmHr"
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.auto_switch_hr || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.auto_switch_hr",
                          e.target.value,
                          "KmHr"
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.auto_switch_km || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.auto_switch_km",
                          e.target.value,
                          "KmHr"
                        )
                      }
                    />
                  </td>
                  <td>
                    <CustomSelect
                      options={tripTypes
                        .filter((type) => type.type === "hr-km")
                        .map((type) => ({ value: type.id, label: type.name }))}
                      value={tripType.rate.auto_switch_trip_type_id}
                      onChange={(option) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.auto_switch_trip_type_id",
                          option.value,
                          "KmHr"
                        )
                      }
                      placeholder="Select"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <h4>Trip Types (KM/Day)</h4>
        <div className="table-container-wrapper">
          <table className="table-container">
            <thead>
              <tr>
                <th>Trip Types</th>
                <th>Base Rate</th>
                <th>Extra KM Rate</th>
                <th>Extra Day Rate</th>
                <th>Auto switch KM</th>
                <th>Auto switch Day</th>
                <th>Auto switch Trip Type</th>
              </tr>
            </thead>
            <tbody>
              {formData.tripTypesKmDay.map((tripType, index) => (
                <tr key={index}>
                  <td>{tripType.name}</td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.base_rates || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.base_rates",
                          e.target.value,
                          "KmDay"
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.extra_km_rates || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.extra_km_rates",
                          e.target.value,
                          "KmDay"
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.extra_day_rates || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.extra_day_rates",
                          e.target.value,
                          "KmDay"
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.auto_switch_km || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.auto_switch_km",
                          e.target.value,
                          "KmDay"
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="1"
                      value={tripType.rate.auto_switch_day || ""}
                      min="0"
                      onChange={(e) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.auto_switch_day",
                          e.target.value,
                          "KmDay"
                        )
                      }
                    />
                  </td>
                  <td>
                    <CustomSelect
                      options={tripTypes
                        .filter((type) => type.type === "day-km")
                        .map((type) => ({ value: type.id, label: type.name }))}
                      value={tripType.rate.auto_switch_trip_type_id}
                      onChange={(option) =>
                        handleInputChangeTripTypes(
                          index,
                          "rate.auto_switch_trip_type_id",
                          option.value,
                          "KmDay"
                        )
                      }
                      placeholder="Select Trip Type"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <fieldset className="fieldset">
          <legend className="legend">Other Details</legend>
          <div className="row">
            <div className="formGroup">
              <label>Overtime Per Hour</label>
              <input
                type="number"
                step="0.01"
                name="overtimePerHour"
                value={formData.extraAllowance.overtime_per_hour || ""}
                placeholder="Enter overtime per hour rate"
                min="0"
                onChange={(e) =>
                  handleInputChangeAllowance(
                    "overtime_per_hour",
                    e.target.value
                  )
                }
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Outstation Allowance</label>
              <input
                type="number"
                step="1"
                name="outstationAllowance"
                value={formData.extraAllowance.outstation_allowance || ""}
                placeholder="Enter outstation allowance"
                min="0"
                onChange={(e) =>
                  handleInputChangeAllowance(
                    "outstation_allowance",
                    e.target.value
                  )
                }
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Sunday Allowance</label>
              <input
                type="number"
                step="1"
                name="sundayAllowance"
                value={formData.extraAllowance.sunday_allowance || ""}
                placeholder="Enter sunday allowance"
                min="0"
                onChange={(e) =>
                  handleInputChangeAllowance("sunday_allowance", e.target.value)
                }
                className="input"
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Night Allowance</label>
              <input
                type="number"
                step="1"
                name="nightAllowance"
                value={formData.extraAllowance.night_allowance || ""}
                placeholder="Enter night allowance"
                min="0"
                onChange={(e) =>
                  handleInputChangeAllowance("night_allowance", e.target.value)
                }
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Driver Daily Allowance</label>
              <input
                type="number"
                step="1"
                name="driverDailyAllowance"
                value={formData.extraAllowance.driver_daily_allowance || ""}
                placeholder="Enter driver daily allowance"
                min="0"
                onChange={(e) =>
                  handleInputChangeAllowance(
                    "driver_daily_allowance",
                    e.target.value
                  )
                }
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Outstation Overtime Allowance</label>
              <input
                type="number"
                step="1"
                name="OutstationOvertimeAllowance"
                value={
                  formData.extraAllowance.outstation_overtime_allowance || ""
                }
                placeholder="Enter outstation overtime allowance"
                min="0"
                onChange={(e) =>
                  handleInputChangeAllowance(
                    "outstation_overtime_allowance",
                    e.target.value
                  )
                }
                className="input"
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Outstation Night Allowance</label>
              <input
                type="number"
                step="1"
                name="OutstationNightAllowance"
                value={formData.extraAllowance.outstation_night_allowance || ""}
                placeholder="Enter outstation overtime allowance"
                min="0"
                onChange={(e) =>
                  handleInputChangeAllowance(
                    "outstation_night_allowance",
                    e.target.value
                  )
                }
                className="input"
              />
            </div>
          </div>
        </fieldset>
        <ShowButton
          permission={PERMISSIONS.VENDOR_CONTRACT_FRONTEND_FORM_UPDATE}
        >
          <button
            type="submit"
            className={`submitButton ${
              isLoading("submitForm") ? "loading" : ""
            }`}
            disabled={isLoading("submitForm")}
          >
            {isLoading("submitForm") ? (
              <LoadingSpinner size="xs" inline={true} message="Saving..." />
            ) : (
              "Submit"
            )}
          </button>
        </ShowButton>
      </form>
    </>
  );
};

export default ContractForm;
